import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabaseClient'
import BANK from './bank'
import { localDateKey, computeJointStreak } from './logic'

// Realtime + initial fetch for the partner's today row. Same shape
// as usePartnerWordleAttempt — channel goes up before the initial
// fetch so we don't miss INSERT/UPDATE events that fire during the
// fetch window. Status buckets:
//   { row: null,                    status: 'not_started' }
//   { row: { question_id, choice_id, … }, status: 'answered' }
export function usePartnerAnswer(partnerId, day) {
  const [row, setRow] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!partnerId || !day) {
      setRow(null)
      setLoading(false)
      return undefined
    }
    let alive = true
    setLoading(true)

    const channel = supabase
      .channel(`questions-partner-${partnerId}-${day}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'couples_question_answers',
          filter: `user_id=eq.${partnerId}`,
        },
        (payload) => {
          if (!alive) return
          const next = payload.new
          if (!next || next.day !== day) return
          setRow(next)
        },
      )
      .subscribe()

    supabase
      .from('couples_question_answers')
      .select('question_id, choice_id, created_at')
      .eq('user_id', partnerId)
      .eq('day', day)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return
        setRow(data || null)
        setLoading(false)
      })

    return () => {
      alive = false
      supabase.removeChannel(channel)
    }
  }, [partnerId, day])

  return { row, status: row ? 'answered' : 'not_started', loading }
}

// Joint streak — both-answered days, contiguous from today back.
// Pulls 120 rows (60 days × 2 users) so a long streak still fits in
// the cap; refreshKey re-runs the query whenever either side's
// answered-today flag changes.
export function useJointAnswerStreak(myUserId, partnerId, refreshKey) {
  const [streak, setStreak] = useState(0)

  useEffect(() => {
    if (!myUserId || !partnerId) {
      setStreak(0)
      return undefined
    }
    let alive = true

    supabase
      .from('couples_question_answers')
      .select('user_id, day')
      .in('user_id', [myUserId, partnerId])
      .order('day', { ascending: false })
      .limit(120)
      .then(({ data }) => {
        if (!alive) return
        const mine = new Set()
        const theirs = new Set()
        for (const r of data || []) {
          if (r.user_id === myUserId) mine.add(r.day)
          else if (r.user_id === partnerId) theirs.add(r.day)
        }
        setStreak(computeJointStreak(mine, theirs))
      })

    return () => { alive = false }
  }, [myUserId, partnerId, refreshKey])

  return streak
}

// Convenience: just today's question for the caller's own user row.
// Caller-side hydrates on mount; the page also writes optimistically
// on submit so the realtime echo doesn't matter for the caller's
// own row, only the partner's.
export function useMyAnswer(userId, day) {
  const [row, setRow] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId || !day) {
      setRow(null)
      setLoading(false)
      return undefined
    }
    let alive = true
    setLoading(true)
    supabase
      .from('couples_question_answers')
      .select('question_id, choice_id, created_at')
      .eq('user_id', userId)
      .eq('day', day)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return
        setRow(data || null)
        setLoading(false)
      })
    return () => { alive = false }
  }, [userId, day])

  // Lets the page push an optimistic write after submit so the UI
  // updates without waiting on a re-fetch.
  return { row, setRow, loading }
}

// Tiny re-export so the page only imports from one place.
export { localDateKey }

// Set<question_id> of every question the pair has answered in the
// last `days` days, STRICTLY BEFORE today so the live in-progress
// day's pick stays stable as new answers land. Used by
// pickDailyQuestion to walk past recently-seen questions instead of
// landing on the same one every couple of days (which the raw
// hash-mod-bank-length approach was doing — birthday paradox on a
// 110-question bank means consecutive collisions show up well
// within a month).
export function useRecentDailyQuestionIds(days = 30) {
  const [ids, setIds] = useState(new Set())

  useEffect(() => {
    let alive = true
    const today = new Date()
    const cutoff = new Date(today)
    cutoff.setDate(cutoff.getDate() - days)
    const cutoffKey = localDateKey(cutoff)
    const todayKey = localDateKey(today)

    supabase
      .from('couples_question_answers')
      .select('question_id')
      .gte('day', cutoffKey)
      .lt('day', todayKey)
      .then(({ data }) => {
        if (!alive) return
        setIds(new Set((data || []).map((r) => r.question_id)))
      })

    return () => { alive = false }
  }, [days])

  return ids
}

// Convert a DB suggestion row into the same shape the static BANK
// uses, so the picker can iterate either kind without branching.
//   row.options is jsonb (an array of { id, text:{en,ar} }).
//   row.text_en + row.text_ar become a unified text:{en,ar} object.
// Prefix the id with "sug-" to guarantee no collisions with the
// bundled BANK ids — both surfaces use it as the stable lookup key.
function normaliseSuggestion(row) {
  if (!row?.id) return null
  return {
    id: `sug-${row.id}`,
    category: row.category,
    text: { en: row.text_en, ar: row.text_ar },
    options: Array.isArray(row.options) ? row.options : [],
    fromSuggestion: true,
    proposerId: row.proposer_id,
  }
}

// Returns the live merged pool — static BANK + every active
// suggestion in the DB. Subscribes to realtime updates so a
// suggestion from the partner appears without a reload (and the
// proposer themself sees their own insert echoed back too).
//
// `pool` is a stable array reference per render; callers can use it
// directly with the existing picker helpers (which accept either
// BANK or the merged pool transparently).
//
// `submit` appends a new suggestion via Supabase; the realtime echo
// flips the row into the local pool when it arrives. Returns the
// inserted row on success, or { error } on failure.
export function useQuestionPool(userId) {
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)

    const channel = supabase
      .channel('couples-question-suggestions')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'couples_question_suggestions' },
        (payload) => {
          if (!alive) return
          if (payload.eventType === 'INSERT' && payload.new && !payload.new.removed_at) {
            setSuggestions((curr) => (
              curr.some((s) => s.id === payload.new.id) ? curr : [...curr, payload.new]
            ))
          } else if (payload.eventType === 'UPDATE' && payload.new) {
            // Treat a removed_at flip as a delete; otherwise update in place.
            if (payload.new.removed_at) {
              setSuggestions((curr) => curr.filter((s) => s.id !== payload.new.id))
            } else {
              setSuggestions((curr) => curr.map((s) => (s.id === payload.new.id ? payload.new : s)))
            }
          } else if (payload.eventType === 'DELETE' && payload.old) {
            setSuggestions((curr) => curr.filter((s) => s.id !== payload.old.id))
          }
        },
      )
      .subscribe()

    supabase
      .from('couples_question_suggestions')
      .select('*')
      .is('removed_at', null)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        if (!alive) return
        setSuggestions(data || [])
        setLoading(false)
      })

    return () => { alive = false; supabase.removeChannel(channel) }
  }, [])

  const submit = useCallback(async ({ category, textEn, textAr, options }) => {
    if (!userId) return { error: { message: 'unauthenticated' } }
    const { data, error } = await supabase
      .from('couples_question_suggestions')
      .insert({
        proposer_id: userId,
        category,
        text_en: textEn,
        text_ar: textAr,
        options,
      })
      .select()
      .single()
    if (error) return { error }
    return { data }
  }, [userId])

  // Merged read-only view of the pool. BANK first so its day-hash
  // ordering stays deterministic; suggestions appended at the end.
  const pool = [
    ...BANK,
    ...suggestions.map(normaliseSuggestion).filter(Boolean),
  ]

  return { pool, suggestions, loading, submit }
}
